import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Grid, Header, List, Segment } from "semantic-ui-react";
import { toast } from "react-toastify";
import { openModal } from "../../../app/common/modals/modalSlice";
import { setPendingNotificationsInDb } from "../../../app/firestore/firestoreService";
import { getDefaultRecipientRoleGroup } from "../../emailTemplates/emailTemplateDefaults";

function actionLabel(action) {
  return action === "signing" ? "Signature requested" : "Shared to view";
}

export default function DocPendingNotifications() {
  const dispatch = useDispatch();
  const { transaction } = useSelector((state) => state.transaction);
  const pendingNotifications = transaction?.pendingNotifications || [];

  if (!pendingNotifications.length) return null;

  // Group notifications by role group: all Buyers together, all Sellers
  // together, and every other role as its own group.
  const groups = {};
  pendingNotifications.forEach((notification) => {
    const groupKey =
      notification.group || getDefaultRecipientRoleGroup(notification.role);
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(notification);
  });

  function handleSend(group, notifications) {
    dispatch(
      openModal({
        modalType: "DocPendingNotificationModal",
        modalProps: { group, notifications },
      })
    );
  }

  async function handleCancel(group, notifications) {
    try {
      const cancelIds = notifications.map((n) => n.id);
      const remaining = (transaction.pendingNotifications || []).filter(
        (n) => !cancelIds.includes(n.id)
      );
      await setPendingNotificationsInDb(transaction.id, remaining);
      toast.success(`Pending notification to ${group} cancelled.`);
    } catch (error) {
      console.error("Error cancelling pending notification:", error);
      toast.error("Failed to cancel notification. Please try again.");
    }
  }

  return (
    <Grid.Column computer={16}>
      <Header className="zero bottom margin">Pending Notifications</Header>
      <p className="text-muted mini bottom margin">
        These share and signature requests are queued. Send a notification to
        each group when you're ready. <i>Their portals are updated.</i>
      </p>
      {Object.entries(groups).map(([group, notifications]) => (
        <Segment key={group} className="mini top margin">
          <Grid stackable verticalAlign="middle">
            <Grid.Column computer={11}>
              <Header as="h4" color="blue" className="mini bottom margin">
                {group}
              </Header>
              <List bulleted>
                {notifications.map((notification) => (
                  <List.Item key={notification.id}>
                    {notification.docName}{" "}
                    <em>— {actionLabel(notification.action)}</em>
                    {notification.role !== group ? ` (${notification.role})` : ""}
                  </List.Item>
                ))}
              </List>
            </Grid.Column>
            <Grid.Column computer={5} textAlign="right">
              <Button
                content="Cancel Notification"
                onClick={() => handleCancel(group, notifications)}
              />
              <Button
                color="blue"
                icon="mail"
                content="Send Notification"
                onClick={() => handleSend(group, notifications)}
              />
            </Grid.Column>
          </Grid>
        </Segment>
      ))}
    </Grid.Column>
  );
}
